/**
 * 도메인 핵심 타입 (02 §4.1). 모든 모듈은 이 엔티티/링크 모델 위의 뷰다.
 * Zod 미러는 lib/schemas/entity.schema.ts.
 */

export type EntityType =
  | 'character'
  | 'planet'
  | 'location'
  | 'faction'
  | 'concept'
  | 'storyline'
  | 'beat'
  | 'theme'
  | 'question'
  | 'note';

export const ENTITY_TYPES: EntityType[] = [
  'character',
  'planet',
  'location',
  'faction',
  'concept',
  'storyline',
  'beat',
  'theme',
  'question',
  'note',
];

/** 구조화 필드 값의 종류 */
export type FieldType =
  | 'text'
  | 'longtext'
  | 'select'
  | 'multiselect'
  | 'number'
  | 'date'
  | 'entityref'
  | 'tags';

export interface FieldDef {
  key: string; // 'wound', 'env' ...
  label: string; // '상처', '환경'
  type: FieldType;
  options?: string[]; // select/multiselect
  group?: string; // 인스펙터 섹션 그룹핑
  order?: number;
  placeholder?: string;
}

/** v1.1 — 캐논 상태 / 확신도 / 출처 (스토리 바이블 프로덕션 필수) */
export type CanonStatus = 'canon' | 'draft' | 'deprecated' | 'question' | 'alternate';
export type Confidence = 'confirmed' | 'probable' | 'speculative';

export const CANON_STATUSES: CanonStatus[] = [
  'canon',
  'draft',
  'deprecated',
  'question',
  'alternate',
];
export const CONFIDENCES: Confidence[] = ['confirmed', 'probable', 'speculative'];

export interface SourceRef {
  kind: 'bible' | 'manuscript' | 'ai' | 'external' | 'note';
  ref: string;
  note?: string;
}

export interface Entity {
  id: string; // nanoid (시드는 안정 id)
  type: EntityType;
  name: string;
  aliases?: string[]; // @멘션·검색용
  summary?: string; // 한 줄 요약
  fields: Record<string, unknown>; // 타입별 + 커스텀 필드 값
  customFields?: FieldDef[]; // 이 엔티티에 추가된 커스텀 필드 정의
  tags?: string[];
  color?: string; // 액센트 오버라이드(없으면 타입 기본)
  icon?: string; // 아이콘 이름 오버라이드(Phosphor)
  refs?: string[]; // 본문 @멘션 역참조(노트 id 등) — 파생 캐시
  meta?: Record<string, unknown>; // 뷰 전용(그래프 좌표 등)
  // --- v1.1 캐논/안전 메타 ---
  canonStatus: CanonStatus;
  confidence: Confidence;
  sourceRefs?: SourceRef[];
  version: number; // 수정마다 +1 (낙관적 동시성·감사)
  deletedAt?: number; // soft delete (기본 목록서 숨김, 복원 가능)
  createdAt: number;
  updatedAt: number;
}

export type LinkType =
  | 'sibling'
  | 'lover'
  | 'rival'
  | 'enemy'
  | 'creator'
  | 'mentor'
  | 'member_of'
  | 'located_in'
  | 'mirror'
  | 'parent_child'
  | 'related';

export const LINK_TYPES: LinkType[] = [
  'sibling',
  'lover',
  'rival',
  'enemy',
  'creator',
  'mentor',
  'member_of',
  'located_in',
  'mirror',
  'parent_child',
  'related',
];

/** 기본적으로 방향성을 가지는 관계(화살표 렌더) */
export const DIRECTED_LINK_TYPES: LinkType[] = [
  'creator',
  'mentor',
  'member_of',
  'located_in',
  'parent_child',
];

export interface Link {
  id: string;
  from: string; // entityId
  to: string; // entityId
  type: LinkType;
  label?: string;
  directed: boolean;
  // --- v1.1 ---
  canonStatus: CanonStatus;
  confidence: Confidence;
  version: number;
  deletedAt?: number;
}
